import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { DownloadWindowWebComponentSchema } from '../types';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { MinLengthValidator } from '../../../ui/gzForm/validators/min-length.validator';
import { MaxLengthValidator } from '../../../ui/gzForm/validators/max-length.validator';
import { RegexValidator } from '../../../ui/gzForm/validators/regex.validator';
import { CustomizableValidator } from '../../../ui/gzForm/validators/customizable.validator';
import { getPasswordRegex } from '../../../utils/expressions.utils';
import { getAccountsStrings } from '../../../accounts/common/accountStrings/accountStrings';
import { AccountConstraints } from '../../../accounts/common/constraints/AccountConstraints';
import { convertUnits } from '../../uploadWindow/utils';
import { translateFn } from '../../../utils/translator.helper';

export class DownloadWindowView extends BaseWebComponentView {
  protected commonStrings: any;
  protected accountsStrings: any;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('#title');
    this.dom.$downloadPathField = this.shadow.querySelector('#download-path-field');
    this.dom.$downloadPathInformation = this.shadow.querySelector('#download-path-information');
    this.dom.$downloadPathInput = this.shadow.querySelector('#download-path-input');
    this.dom.$passwordProtectLabel = this.shadow.querySelector('#password-protect-label');
    this.dom.$fileArchiveDetailsInformation = this.shadow.querySelector('#file-archive-details-information');
    this.dom.$passwordProtectToggle = this.shadow.querySelector('#password-protect');
    this.dom.$passwordProtectFields = this.shadow.querySelector('#password-protect-fields');
    this.dom.$archivePasswordFormField = this.shadow.querySelector('#archive-password-form-field');
    this.dom.$archivePasswordInput = this.shadow.querySelector('#archive-password');
    this.dom.$repeatPasswordFormField = this.shadow.querySelector('#repeat-password-form-field');
    this.dom.$repeatPasswordInput = this.shadow.querySelector('#repeat-password');

    this.dom.$warningPassword = this.shadow.querySelector('#warning-password');
    this.dom.$archivePasswordDetails = this.shadow.querySelector('#archive-password-details');

    this.getTranslations();
    this.setTranslatedStrings();
  }

  buildSuccessView(data: DownloadWindowWebComponentSchema) {
    this.dom.$downloadPathField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.dom.$downloadPathField.value = data.path;
    this.dom.$downloadPathField.manualValidation = true;

    this.setTextForDownloadPathInformation(data.maxFilesSize);
    this.setPasswordValidators();
  }

  public setPasswordValidators() {
    this.dom.$archivePasswordFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinLengthValidator(AccountConstraints.Password.MinLength, this.accountsStrings.passwordMinLength),
      MaxLengthValidator(AccountConstraints.Password.MaxLength, this.accountsStrings.passwordMaxLength),
      RegexValidator(getPasswordRegex(), this.accountsStrings.invalidPassword),
    ];

    this.dom.$repeatPasswordFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      CustomizableValidator(value => {
        if (value !== this.dom.$archivePasswordFormField.value) {
          return {
            valid: false,
            error: this.translations.passwordsDoNotMatch,
          };
        }
        return {
          valid: true,
        };
      }),
    ];
  }

  private setTextForDownloadPathInformation(maxFilesSize: number) {
    this.translations.downloadPathInformation = translateFn(
      'Specify the path to the file or folder. If you specify the path to a folder, all containing files are stored in a file archive and downloaded. The overall download size cannot exceed {MAX_SIZE}.',
      { MAX_SIZE: convertUnits(maxFilesSize) }
    );
    this.dom.$downloadPathInformation.innerText = this.translations.downloadPathInformation;
  }

  public setTranslatedStrings() {
    this.dom.$title.innerText = this.translations.title;
    this.dom.$downloadPathField.label = this.translations.downloadPathLabel;
    this.dom.$passwordProtectLabel.innerText = this.translations.passwordProtectLabel;
    this.dom.$fileArchiveDetailsInformation.innerHTML = this.translations.fileArchiveDetailsInformation;
    this.dom.$passwordProtectToggle.textContent = this.translations.passwordProtectToggle;

    this.dom.$archivePasswordFormField.label = this.translations.archivePasswordLabel;
    this.dom.$repeatPasswordFormField.label = this.translations.repeatPasswordLabel;

    this.dom.$warningPassword.textContent = this.translations.warningPassword;
    this.dom.$archivePasswordDetails.textContent = this.translations.archivePasswordDetails;
  }

  public getTranslations() {
    this.translations = this.stringsTranslator.getDownloadWindowTranslations();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.accountsStrings = getAccountsStrings();

    return this.translations;
  }

  public checkPasswordProtectForms() {
    const passwordProtectValue = this.dom.$passwordProtectToggle.getFormValue();

    if (passwordProtectValue) {
      toggleVisibility(this.dom.$passwordProtectFields, true);
      this.dom.$archivePasswordFormField.value = '';
      this.dom.$repeatPasswordFormField.value = '';
    } else {
      toggleVisibility(this.dom.$passwordProtectFields, false);
      this.dom.$archivePasswordFormField.value = '';
      this.dom.$repeatPasswordFormField.value = '';
    }
  }
}
